import logger from '@helpers/utils/logger/client';
import { eventsCatalogPayload } from '@hooks/analytics/use-analytic-events';

// Delay after pageview before sending banner events (ensures pageview completes first)
const BANNER_EVENT_DELAY_MS = 1000;

export function getPerformanceTimestamp(): string {
  return typeof window !== 'undefined' ? performance.now().toFixed(2) : Date.now().toString();
}

export function getFormattedTimestamp(): string {
  return new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export function getCombinedTimestamp(): string {
  const formatted = getFormattedTimestamp();
  const performance = getPerformanceTimestamp();
  return `[${formatted}] [${performance}ms]`;
}

function getCampaignInfo(): Record<string, string | null> {
  if (typeof window === 'undefined') {
    return {};
  }

  const tcVars = (window.tc_vars || {}) as Record<string, unknown>;
  const urlParams = new URLSearchParams(window.location.search);

  const keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'gbraid',
    'wbraid',
    'srsltid',
  ];

  return keys.reduce(
    (acc, key) => {
      const campaignKey = key.startsWith('utm_') ? `campaign_${key.slice(4)}` : key;
      acc[campaignKey] = urlParams.get(key) || (tcVars[campaignKey] as string | undefined) || null;
      return acc;
    },
    {} as Record<string, string | null>,
  );
}

function getFullPayload(eventCategory?: string, eventName?: string, eventPayload?: unknown) {
  if (!eventCategory || !eventName) {
    return eventPayload as Record<string, unknown>;
  }
  const catalogPayload = eventsCatalogPayload[eventCategory]?.[eventName];
  return { ...catalogPayload, ...(eventPayload as Record<string, unknown>), event: eventName };
}

function isBannerEvent(eventName?: string): boolean {
  if (!eventName) {
    return false;
  }
  return eventName.startsWith('banner_app_') || eventName.startsWith('welcome_offer_');
}

// Temporary: to handle SEM PDP banner event ordering
// On SEM PDP ProductPage, defers callback execution to ensure pageview is sent first
export function executeAfterPageviewIfSemPDP(
  callback: () => void,
  isSemPDP: boolean,
  pageType?: string,
  eventName?: string,
  eventPayload?: unknown,
  eventCategory?: string,
  isPageviewEnabled?: boolean,
): void {
  // When FF is off, TagCommander handles pageview automatically, so execute callback immediately
  if (isSemPDP && pageType === 'ProductPage' && isPageviewEnabled) {
    setTimeout(() => {
      callback();

      if (isBannerEvent(eventName)) {
        const fullPayload = getFullPayload(eventCategory, eventName, eventPayload);
        logger.info(`${getCombinedTimestamp()} [SEM PDP BANNER] Banner event sent: ${eventName}`, {
          ...fullPayload,
          campaign_info: getCampaignInfo(),
        });
      }
    }, BANNER_EVENT_DELAY_MS);
  } else {
    // FF off or not SEM PDP ProductPage - execute immediately (normal behavior)
    callback();
  }
}
